import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import useServerPagination from '@/hooks/useServerPagination';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { IPluginDetailResponse, IRateData } from '@/providers/HttpProvider/plugins/MLFulfill/types';
import { RootState } from '@/redux/store';
import { LocalConfig, Order, UserPlugin } from '@/types/plugins/mlFulfill/orders.interface';
import { Product } from '@/types/plugins/mlFulfill/products.interface';
import { Warehouse } from '@/types/plugins/mlFulfill/warehouses.interface';

import { Dimensions } from './dependencies/modals/DimensionsModal';

export interface IFilter {
  startDate: string;
  endDate: string;
  status: string;
}

const initialFilters: IFilter = { startDate: '', endDate: '', status: '' };

interface OrdersState {
  data: Order[];
  total: number;
  loading: boolean;
  error: boolean;
}

interface ProductsState {
  data: Product[];
  total: number;
  loading: boolean;
}

interface WarehousesState {
  data: Warehouse[];
  loading: boolean;
  saving: boolean;
}

interface ModalState {
  rate: boolean;
  createOrder: boolean;
  details: boolean;
}

const useMLFulfill = () => {
  const { data: profile } = useSelector((state: RootState) => state.parcel.profile);
  const clientId = profile?.credentials.clientId ?? '';

  const [activeTab, setActiveTab] = useState(0);

  // --- Orders ---
  const pagination = useServerPagination();
  const [orders, setOrders] = useState<OrdersState>({
    data: [],
    total: 0,
    loading: false,
    error: false,
  });
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [rateData, setRateData] = useState<IRateData | null>(null);
  const [loadingRate, setLoadingRate] = useState(false);
  const [modals, setModals] = useState<ModalState>({
    rate: false,
    createOrder: false,
    details: false,
  });

  // --- Orders: filters + search ---
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState<IFilter>(initialFilters);
  const [searchValue, setSearchValue] = useState('');

  // --- Products ---
  const productPagination = useServerPagination();
  const [products, setProducts] = useState<ProductsState>({ data: [], total: 0, loading: false });
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());
  const [syncingCatalog, setSyncingCatalog] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [dimensionsModal, setDimensionsModal] = useState<{
    open: boolean;
    productId: string | null;
    actionType: 'sync' | 'me1';
    initialDimensions: Dimensions | null;
  }>({ open: false, productId: null, actionType: 'sync', initialDimensions: null });
  const [convertingMe1Ids, setConvertingMe1Ids] = useState<Set<string>>(new Set());
  const [isMe1AsTailorMadeEnabled, setIsMe1AsTailorMadeEnabled] = useState(false);

  // --- Warehouses ---
  const [warehouses, setWarehouses] = useState<WarehousesState>({
    data: [],
    loading: false,
    saving: false,
  });
  const [activeWarehouseIds, setActiveWarehouseIds] = useState<string[]>([]);

  const handleModal = (name: keyof ModalState) => {
    setModals((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleOpenDetails = (order: Order) => {
    setSelectedOrder(order);
    setModals((prev) => ({ ...prev, details: true }));
  };

  const handleCloseDetails = () => {
    setModals((prev) => ({ ...prev, details: false }));
    setSelectedOrder(null);
  };

  // --- Orders: filter handlers ---
  const onChangeFilter = useCallback((filterName: string, value: string | null) => {
    setFilters((prev) => ({ ...prev, [filterName as keyof IFilter]: value ?? '' }));
  }, []);

  const clearFilters = () => {
    pagination.resetFilters();
    setFilters(initialFilters);
    setSearchValue('');
    setOpenFilters(false);
    fetchOrders(initialFilters);
  };

  const applyFilters = () => {
    setOpenFilters(false);
    fetchOrders(filters);
  };

  const onClose = () => setOpenFilters(false);

  const handleSearchChange = (value: string) => setSearchValue(value);

  // --- Orders: fetch ---
  const fetchOrders = async (activeFilters?: IFilter) => {
    if (!clientId) return;
    const f = activeFilters ?? filters;
    setOrders((prev) => ({ ...prev, loading: true, error: false }));
    try {
      const http = initializeHttpProvider();
      const res = await http.mlFulfill.orders({
        clientId,
        page: pagination.filters.page as number,
        pageSize: pagination.filters.page_size as number,
        ...(f.status && { status: f.status }),
        ...(f.startDate && { dateFrom: f.startDate }),
        ...(f.endDate && { dateTo: f.endDate }),
      });
      if ('data' in res && res.success) {
        const payload = res.data as { data: Order[]; total: number };
        setOrders({ data: payload.data, total: payload.total, loading: false, error: false });
      } else {
        setOrders((prev) => ({ ...prev, loading: false, error: true }));
      }
    } catch {
      setOrders((prev) => ({ ...prev, loading: false, error: true }));
      toast.error('Error al cargar órdenes de Fulfill');
    }
  };

  // --- Orders: search by orderId (backend) ---
  const handleSearch = async () => {
    const orderId = searchValue.trim();
    if (!orderId) {
      fetchOrders();
      return;
    }
    if (!clientId) return;
    setOrders((prev) => ({ ...prev, loading: true, error: false }));
    try {
      const http = initializeHttpProvider();
      const res = await http.mlFulfill.orderById({ orderId, clientId });
      if ('data' in res && res.success) {
        const data = (res as { data: Order[]; success: boolean }).data;
        const list = Array.isArray(data) ? data : [data];
        setOrders({ data: list, total: list.length, loading: false, error: false });
      } else {
        setOrders({ data: [], total: 0, loading: false, error: true });
        toast.error('Orden no encontrada');
      }
    } catch {
      setOrders((prev) => ({ ...prev, loading: false, error: true }));
      toast.error('Error al buscar la orden');
    }
  };

  // --- Orders: rate check ---
  const handleRateCheck = async (id: string) => {
    if (!clientId) return;
    setLoadingRate(true);
    handleModal('rate');
    try {
      const http = initializeHttpProvider();
      const res = await http.mlFulfill.rate({ id, clientId });
      if ('data' in res && res.success) {
        setRateData((res as { data: IRateData }).data);
      } else {
        toast.error('Sin cobertura para esta orden');
        handleModal('rate');
      }
    } catch {
      toast.error('Error al verificar cobertura');
      handleModal('rate');
    } finally {
      setLoadingRate(false);
    }
  };

  // --- Orders: create order ---
  const handleCreateOrder = async (id: string) => {
    if (!clientId) return;
    try {
      const http = initializeHttpProvider();
      const res = await http.mlFulfill.createOrder({ id, clientId });
      if ('success' in res && res.success) {
        toast.success('Orden creada en 99minutos Fulfill');
        handleModal('rate');
        fetchOrders();
      } else {
        toast.error('Error al crear la orden');
      }
    } catch {
      toast.error('Error al crear la orden');
    }
  };

  // --- Orders: create order direct ---
  const [creatingOrderIds, setCreatingOrderIds] = useState<Set<string>>(new Set());

  const handleDirectCreate = async (id: string) => {
    if (!clientId) return;
    setCreatingOrderIds((prev) => new Set(prev).add(id));
    try {
      const http = initializeHttpProvider();
      const res = await http.mlFulfill.createOrder({ id, clientId });
      if ('success' in res && res.success) {
        toast.success('Orden creada en 99minutos Fulfill');
        fetchOrders();
      } else {
        toast.error('Error al crear la orden');
      }
    } catch {
      toast.error('Error al crear la orden');
    } finally {
      setCreatingOrderIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // --- Products: fetch ---
  const fetchProducts = async () => {
    if (!clientId) return;
    setProducts((prev) => ({ ...prev, loading: true }));
    try {
      const http = initializeHttpProvider();
      const res = await http.mlFulfill.products({
        clientId,
        page: productPagination.filters.page as number,
        pageSize: productPagination.filters.page_size as number,
      });
      if ('data' in res && res.success) {
        const payload = res.data as { data: Product[]; total: number };
        const sorted = [...payload.data].sort((a, b) => {
          const ta = a.start_time ? new Date(a.start_time).getTime() : 0;
          const tb = b.start_time ? new Date(b.start_time).getTime() : 0;
          const validA = isNaN(ta) ? 0 : ta;
          const validB = isNaN(tb) ? 0 : tb;
          return validB - validA;
        });
        setProducts({ data: sorted, total: payload.total, loading: false });
      } else {
        setProducts((prev) => ({ ...prev, loading: false }));
      }
    } catch {
      setProducts((prev) => ({ ...prev, loading: false }));
      toast.error('Error al cargar productos');
    }
  };

  // --- Products: sync single ---
  const syncProduct = async (
    productId: string,
    dims: { height: number; width: number; length: number; weight: number },
  ) => {
    if (!clientId) return;
    setSyncingIds((prev) => new Set(prev).add(productId));
    try {
      const http = initializeHttpProvider();
      const res = await http.mlFulfill.syncProduct({ productId, clientId, ...dims });
      if ('success' in res && res.success) {
        toast.success('Producto sincronizado con Fulfill');
        fetchProducts();
      } else {
        toast.error('Error al sincronizar producto');
      }
    } catch {
      toast.error('Error al sincronizar producto');
    } finally {
      setSyncingIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  const parseDimensions = (raw: string) => {
    const m = raw.match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?),(\d+(?:\.\d+)?)$/);
    if (!m) return null;
    return {
      height: Math.round(Number(m[1])),
      width: Math.round(Number(m[2])),
      length: Math.round(Number(m[3])),
      weight: Math.round(Number(m[4])),
    };
  };

  const handleSyncProduct = (productId: string) => {
    const product = products.data.find((p) => p.id === productId);
    const dims = product?.shipping?.dimensions
      ? parseDimensions(product.shipping.dimensions)
      : null;
    if (dims) {
      syncProduct(productId, dims);
    } else {
      setDimensionsModal({
        open: true,
        productId,
        actionType: 'sync',
        initialDimensions: null,
      });
    }
  };

  const handleOpenConvertToMe1 = (productId: string) => {
    const product = products.data.find((p) => p.id === productId);
    const dims = product?.shipping?.dimensions
      ? parseDimensions(product.shipping.dimensions)
      : null;
    setDimensionsModal({
      open: true,
      productId,
      actionType: 'me1',
      initialDimensions: dims,
    });
  };

  const convertToMe1 = async (
    productId: string,
    dims: { height: number; width: number; length: number; weight: number },
  ) => {
    if (!clientId) return;
    setConvertingMe1Ids((prev) => new Set(prev).add(productId));
    try {
      const http = initializeHttpProvider();
      const res = await http.mlFulfill.convertToMe1({
        productId,
        clientId,
        height: dims.height,
        width: dims.width,
        length: dims.length,
        weight: dims.weight,
        localPickUp: false,
      });
      if ('success' in res && res.success) {
        toast.success(res.message || 'Modalidad de la publicación actualizada a ME1');
        fetchProducts();
      } else {
        toast.error('Error al convertir la publicación a ME1');
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Error al convertir la publicación a ME1';
      toast.error(message);
    } finally {
      setConvertingMe1Ids((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  const handleConfirmDimensions = (dims: {
    height: number;
    width: number;
    length: number;
    weight: number;
  }) => {
    if (!dimensionsModal.productId) return;
    const targetProductId = dimensionsModal.productId;
    const action = dimensionsModal.actionType;
    setDimensionsModal({
      open: false,
      productId: null,
      actionType: 'sync',
      initialDimensions: null,
    });
    if (action === 'me1') {
      convertToMe1(targetProductId, dims);
    } else {
      syncProduct(targetProductId, dims);
    }
  };

  const handleCloseDimensionsModal = () => {
    setDimensionsModal({
      open: false,
      productId: null,
      actionType: 'sync',
      initialDimensions: null,
    });
  };

  const isProductSyncable = (p: Product) => p.status === 'active' && p.shipping?.mode !== 'me2';

  const buildSyncPayload = (list: Product[]) =>
    list
      .filter((p) => isProductSyncable(p) && p.shipping?.dimensions)
      .map((p) => {
        const d = parseDimensions(p.shipping!.dimensions!);
        return d ? { productId: p.id, ...d } : null;
      })
      .filter(Boolean) as {
      productId: string;
      height: number;
      width: number;
      length: number;
      weight: number;
    }[];

  // --- Products: sync catalog ---
  const handleSyncCatalog = async () => {
    if (!clientId) return;

    const payload = buildSyncPayload(products.data);
    if (payload.length === 0) {
      toast.info('No hay productos con dimensiones elegibles para sincronizar');
      return;
    }

    setSyncingCatalog(true);
    try {
      const http = initializeHttpProvider();
      const res = await http.mlFulfill.syncCatalog({ clientId, products: payload });
      if ('success' in res && res.success) {
        toast.success('Catálogo sincronizado con Fulfill');
        fetchProducts();
      } else {
        toast.error('Error al sincronizar catálogo');
      }
    } catch {
      toast.error('Error al sincronizar catálogo');
    } finally {
      setSyncingCatalog(false);
    }
  };

  // --- Products: sync selected ---
  const handleSyncSelected = async () => {
    if (!clientId || selectedProductIds.length === 0) return;

    const selected = products.data.filter((p) => selectedProductIds.includes(p.id));
    const payload = buildSyncPayload(selected);
    if (payload.length === 0) {
      toast.info('Ninguno de los productos seleccionados tiene dimensiones registradas');
      return;
    }

    setSyncingCatalog(true);
    try {
      const http = initializeHttpProvider();
      const res = await http.mlFulfill.syncCatalog({ clientId, products: payload });
      if ('success' in res && res.success) {
        toast.success(`${payload.length} producto(s) sincronizado(s) con Fulfill`);
        setSelectedProductIds([]);
        fetchProducts();
      } else {
        toast.error('Error al sincronizar productos seleccionados');
      }
    } catch {
      toast.error('Error al sincronizar productos seleccionados');
    } finally {
      setSyncingCatalog(false);
    }
  };

  // --- Warehouses: fetch ---
  const fetchWarehouses = async () => {
    if (!clientId) return;
    setWarehouses((prev) => ({ ...prev, loading: true }));
    try {
      const http = initializeHttpProvider();
      const res = await http.mlFulfill.warehouses({ clientId });
      if ('data' in res && res.success) {
        const data = res.data as Warehouse[];
        setWarehouses({ data, loading: false, saving: false });
        setActiveWarehouseIds(data.filter((w) => w.active).map((w) => w.warehouseId));
      } else {
        setWarehouses((prev) => ({ ...prev, loading: false }));
      }
    } catch {
      setWarehouses((prev) => ({ ...prev, loading: false }));
      toast.error('Error al cargar almacenes');
    }
  };

  const handleToggleWarehouse = (warehouseId: string) => {
    setActiveWarehouseIds((prev) =>
      prev.includes(warehouseId) ? prev.filter((id) => id !== warehouseId) : [...prev, warehouseId],
    );
  };

  const handleSaveWarehouses = async () => {
    if (!clientId) return;
    setWarehouses((prev) => ({ ...prev, saving: true }));
    try {
      const http = initializeHttpProvider();
      const res = await http.mlFulfill.updateWarehouses({
        clientId,
        warehouseIds: activeWarehouseIds,
      });
      if ('success' in res && res.success) {
        toast.success('Almacenes actualizados correctamente');
        fetchWarehouses();
      } else {
        toast.error('Error al actualizar almacenes');
      }
    } catch {
      toast.error('Error al actualizar almacenes');
    } finally {
      setWarehouses((prev) => ({ ...prev, saving: false }));
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (clientId) {
      void (async () => {
        try {
          const http = initializeHttpProvider();
          const res = await http.mlFulfill.getUserPlugins({ clientId });
          if (!isMounted) return;
          if ('data' in res && res.success && Array.isArray(res.data)) {
            const fulfillPlugin = (res.data as UserPlugin[]).find((p) =>
              p.platform.toLowerCase().includes('fulfillment'),
            );
            if (fulfillPlugin?.id) {
              const detailsRes = await http.mlFulfill.getPluginDetails({
                clientId,
                pluginId: fulfillPlugin.id,
              });
              if (!isMounted) return;
              if (
                'data' in detailsRes &&
                detailsRes.success &&
                !Array.isArray(detailsRes.data) &&
                'localConfig' in detailsRes.data
              ) {
                const { localConfig } = (detailsRes as IPluginDetailResponse).data;
                const isMe1 = Boolean(
                  localConfig?.me1AsTailorMade ??
                  (localConfig as Record<string, unknown>)?.ME1AsTailorMade,
                );
                setIsMe1AsTailorMadeEnabled(isMe1);
                return;
              }
            }
            const fallbackConfig = fulfillPlugin?.localConfig as
              (LocalConfig & { me1AsTailorMade?: boolean; ME1AsTailorMade?: boolean }) | undefined;
            const isMe1Fallback = Boolean(
              fallbackConfig?.me1AsTailorMade ?? fallbackConfig?.ME1AsTailorMade,
            );
            setIsMe1AsTailorMadeEnabled(isMe1Fallback);
          }
        } catch {
          if (isMounted) setIsMe1AsTailorMadeEnabled(false);
        }
      })();
    }
    return () => {
      isMounted = false;
    };
  }, [clientId]);

  useEffect(() => {
    if (activeTab === 0) fetchOrders();
  }, [activeTab, pagination.filters.page, pagination.filters.page_size, clientId]);

  useEffect(() => {
    if (activeTab === 1) fetchProducts();
  }, [activeTab, productPagination.filters.page, productPagination.filters.page_size, clientId]);

  useEffect(() => {
    if (activeTab === 2 && clientId) fetchWarehouses();
  }, [activeTab, clientId]);

  return {
    clientId,
    activeTab,
    setActiveTab,
    // orders
    orders,
    pagination,
    openFilters,
    setOpenFilters,
    filters,
    onChangeFilter,
    clearFilters,
    applyFilters,
    onClose,
    searchValue,
    handleSearchChange,
    handleSearch,
    selectedOrder,
    rateData,
    loadingRate,
    modals,
    handleModal,
    handleOpenDetails,
    handleCloseDetails,
    handleRateCheck,
    handleCreateOrder,
    handleDirectCreate,
    creatingOrderIds,
    fetchOrders,
    // products
    products,
    productPagination,
    selectedProductIds,
    setSelectedProductIds,
    syncingIds,
    syncingCatalog,
    handleSyncProduct,
    handleSyncCatalog,
    handleSyncSelected,
    dimensionsModal,
    handleConfirmDimensions,
    handleCloseDimensionsModal,
    isMe1AsTailorMadeEnabled,
    convertingMe1Ids,
    handleOpenConvertToMe1,
    // warehouses
    warehouses,
    activeWarehouseIds,
    handleToggleWarehouse,
    handleSaveWarehouses,
  };
};

export default useMLFulfill;
